import React, { useState, useEffect } from 'react';
import { Menu, X, ChevronDown } from 'lucide-react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

interface NavItem {
  label: string;
  to?: string;
  action?: () => void;
  items?: {
    label: string;
    to?: string;
    action?: () => void;
  }[];
  isButton?: boolean;
}

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileDropdowns, setMobileDropdowns] = useState<string[]>([]);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isHomePage = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    setIsOpen(false);
    setOpenDropdown(null);
    setMobileDropdowns([]);

    if (!isHomePage) {
      navigate('/', { replace: true });
      setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
      return;
    }

    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const navItems: NavItem[] = [
    { label: 'Home', to: '/' },
    {
      label: 'About',
      items: [
        { label: 'Who We Are', action: () => scrollToSection('about') },
        { label: 'Statement of Faith', to: '/statement-of-faith' },
        { label: 'Salvation', to: '/salvation' }
      ]
    },
    {
      label: 'Resources',
      items: [
        { label: 'Sermons', to: '/sermons' },
        { label: 'Live Stream', action: () => scrollToSection('live-stream') },
        { label: 'Tracts', to: '/tracts' }
      ]
    },
    {
      label: 'Connect',
      items: [
        { label: 'Prayer Requests', to: '/prayer-request' },
        { label: 'Share Testimony', to: '/share-testimony' },
        { label: 'Newsletter', action: () => document.getElementById('newsletter')?.scrollIntoView({ behavior: 'smooth' }) },
        { label: 'Get in Touch', action: () => scrollToSection('contact') }
      ]
    },
    {
      label: 'Give',
      action: () => scrollToSection('give'),
      isButton: true
    }
  ];

  useEffect(() => {
    setIsOpen(false);
    setOpenDropdown(null);
    setMobileDropdowns([]);
    if (!location.hash) {
      window.scrollTo(0, 0);
    }
  }, [location]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (openDropdown && !(event.target as Element).closest('.dropdown')) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [openDropdown]);

  const toggleMobileDropdown = (label: string) => {
    setMobileDropdowns(prev =>
      prev.includes(label)
        ? prev.filter(item => item !== label)
        : [...prev, label]
    );
  };

  const renderNavItem = (item: NavItem) => {
    if (item.isButton) {
      return (
        <button
          onClick={item.action}
          className="bg-primary-600 text-white px-5 py-2.5 rounded-full hover:bg-primary-700 transition-all duration-200 hover:shadow-lg hover:shadow-primary-600/20 active:scale-[0.97] font-medium text-sm"
        >
          {item.label}
        </button>
      );
    }

    if (item.items) {
      return (
        <div className="relative dropdown">
          <button
            onClick={() => setOpenDropdown(openDropdown === item.label ? null : item.label)}
            className="flex items-center text-gray-700 hover:text-primary-600 transition-colors font-medium text-sm py-2"
          >
            {item.label}
            <ChevronDown
              size={14}
              className={`ml-1 transform transition-transform duration-200 ${
                openDropdown === item.label ? 'rotate-180' : ''
              }`}
            />
          </button>
          {openDropdown === item.label && (
            <div className="absolute z-10 mt-2 w-52 bg-white rounded-2xl shadow-elevated py-2 border border-gray-100/80 animate-slide-down overflow-hidden">
              {item.items.map((subItem) => (
                <div key={subItem.label}>
                  {subItem.to ? (
                    <Link
                      to={subItem.to}
                      className="block px-4 py-2.5 text-sm text-gray-600 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                    >
                      {subItem.label}
                    </Link>
                  ) : (
                    <button
                      onClick={subItem.action}
                      className="block w-full text-left px-4 py-2.5 text-sm text-gray-600 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                    >
                      {subItem.label}
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (item.to) {
      return (
        <Link
          to={item.to}
          className="text-gray-700 hover:text-primary-600 transition-colors font-medium text-sm"
        >
          {item.label}
        </Link>
      );
    }

    return (
      <button
        onClick={item.action}
        className="text-gray-700 hover:text-primary-600 transition-colors font-medium text-sm"
      >
        {item.label}
      </button>
    );
  };

  return (
    <nav className={`fixed w-full z-50 transition-all duration-300 ${
      scrolled
        ? 'bg-white/95 backdrop-blur-xl shadow-soft'
        : 'bg-white/90 backdrop-blur-md'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-18 items-center py-3">
          <div className="flex items-center">
            <Link to="/" className="flex items-center group">
              <img
                src="https://hcamiahiytrjczzsrodb.supabase.co/storage/v1/object/public/etwcministry//ETWC%20Logo.avif"
                alt="ETWC Ministry Logo"
                className="h-14 w-auto transition-transform duration-200 group-hover:scale-105"
              />
            </Link>
          </div>

          <div className="hidden md:flex items-center space-x-7">
            {navItems.map((item) => (
              <div key={item.label}>{renderNavItem(item)}</div>
            ))}
          </div>

          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-gray-700 p-2 rounded-xl hover:bg-gray-100 transition-colors"
              aria-label="Toggle menu"
            >
              {isOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`md:hidden fixed inset-x-0 top-[4.5rem] bg-white/98 backdrop-blur-xl shadow-elevated border-t border-gray-100 overflow-y-auto transition-all duration-300 ease-out ${
          isOpen ? 'max-h-[calc(100vh-4.5rem)] opacity-100 visible' : 'max-h-0 opacity-0 invisible'
        }`}
        style={{ zIndex: 40 }}
      >
        <div className="divide-y divide-gray-50 px-2 py-2">
          {navItems.map((item) => (
            <div key={item.label} className="px-2">
              {item.items ? (
                <div>
                  <button
                    onClick={() => toggleMobileDropdown(item.label)}
                    className="flex items-center justify-between w-full py-3.5"
                  >
                    <span className="text-[15px] font-medium text-gray-900">{item.label}</span>
                    <ChevronDown
                      size={18}
                      className={`text-gray-400 transform transition-transform duration-200 ${
                        mobileDropdowns.includes(item.label) ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                  <div
                    className={`overflow-hidden transition-all duration-300 ease-out ${
                      mobileDropdowns.includes(item.label)
                        ? 'max-h-96 opacity-100 pb-2'
                        : 'max-h-0 opacity-0'
                    }`}
                  >
                    <div className="space-y-0.5 pl-3 border-l-2 border-primary-100 ml-1">
                      {item.items.map((subItem) => (
                        <div key={subItem.label}>
                          {subItem.to ? (
                            <Link
                              to={subItem.to}
                              className="block py-2.5 px-3 text-[15px] text-gray-600 hover:text-primary-600 rounded-lg hover:bg-primary-50/50 transition-colors"
                              onClick={() => {
                                setIsOpen(false);
                                setMobileDropdowns([]);
                              }}
                            >
                              {subItem.label}
                            </Link>
                          ) : (
                            <button
                              onClick={() => {
                                subItem.action?.();
                                setIsOpen(false);
                                setMobileDropdowns([]);
                              }}
                              className="block w-full text-left py-2.5 px-3 text-[15px] text-gray-600 hover:text-primary-600 rounded-lg hover:bg-primary-50/50 transition-colors"
                            >
                              {subItem.label}
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : item.isButton ? (
                <div className="py-3">
                  <button
                    onClick={() => {
                      item.action?.();
                      setIsOpen(false);
                    }}
                    className="w-full bg-primary-600 text-white px-6 py-3 rounded-xl hover:bg-primary-700 transition-all text-center font-medium"
                  >
                    {item.label}
                  </button>
                </div>
              ) : (
                <Link
                  to={item.to || '#'}
                  onClick={() => {
                    if (item.action) {
                      item.action();
                    }
                    setIsOpen(false);
                  }}
                  className="block py-3.5 text-[15px] font-medium text-gray-900"
                >
                  {item.label}
                </Link>
              )}
            </div>
          ))}
        </div>
      </div>
    </nav>
  );
}
